import { v4 as uuidv4 } from 'uuid'

import { VariableSelectFeatureFlag } from '@makyoapp/makyo-common'

import { IBuilderComponent } from '../../base'
import { BaseAiComponentInput } from '../../registry'
import type {
  AiAspectRatio,
  OverflowSettings,
  ResponsiveValue,
  SpacingInputValue,
  TextAlignment,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import {
  formatAspectRatioFormat,
  formatCornerRadiusFormat,
  formatFromVariableSelect,
  formatFromVariableText,
  formatSpacingFormat,
  formatWidthFormat,
} from '../../format-utils'
import {
  deepMerge,
  parseAlignmentFormat,
  parseAspectRatioFormat,
  parseCornerRadiusFormat,
  parseResponsiveFormat,
  parseSpacingFormat,
  parseToVariableText,
  parseWidthFormat,
} from '../../parser-utils'
import videoTemplate from './video.template.json'

export interface IVideoComponentSettings {
  // Data settings
  'video-data-src': VariableSelectValue
  'video-data-title': VariableTextValue
  'video-data-cover': any

  // Design settings
  'video-design-scheme': any
  'video-design-color-variant': string
  'video-design-progress': any
  'video-design-controls': boolean
  'video-design-volume': boolean
  'video-design-width': OverflowSettings<ResponsiveValue<string[] | number[]>>
  'video-design-shape': string
  'video-design-margin': OverflowSettings<ResponsiveValue<SpacingInputValue>>
  'video-design-padding': ResponsiveValue<SpacingInputValue>
  'video-design-h-align': ResponsiveValue<TextAlignment>
  'video-design-ratio': ResponsiveValue<AiAspectRatio>
  'video-design-hover-effect'?: string // Hover effect: 'none' | 'underline' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

export interface IVideoComponent extends IBuilderComponent {
  componentId: 'video'
  groupId: 'media'
  settings: IVideoComponentSettings
}

export interface AiVideoInput extends BaseAiComponentInput {
  src: string // Video URL (YouTube, Vimeo, or direct video URL)
  videoTitle?: string[] // Format: ["text:Video Title"] or ["text:Watch ", "cr:videoName"]
  controls?: boolean // Show video controls (default: true)
  volume?: boolean // Show volume control (default: true)
  width?: string // Format: "w:100%" or "w:300px" or "w:100% de:w:50%"
  margin?: string // Format: "m:10px" or "mt:10px mb:10px"
  padding?: string // Format: "p:10px"
  align?: string // Format: "h:center" or "h:start ta:h:center"
  cornerRadius?: string // Format: "large" or "small ta:medium de:large"
  aspectRatio?: string // Format: "16:9" or "1:1 ta:16:9"
  hoverEffect?: string // Format: "bounce" or "underline" | 'none' | 'lift' | 'scale' | 'dim' | 'skew' | 'rotate' | 'glow' | 'shimmer' | 'border' | 'squeeze' | 'blur' | 'neon' | 'flip' | 'bounce'
}

const VIDEO_DEFAULT_TEMPLATE = videoTemplate as unknown as IVideoComponentSettings

export class VideoComp {
  getTemplate(): IVideoComponentSettings {
    return VIDEO_DEFAULT_TEMPLATE
  }

  /**
   * format video component settings to AI video input
   */
  format(settings: IVideoComponentSettings): AiVideoInput {
    const template = this.getTemplate()
    const result: AiVideoInput = {
      type: 'video',
      src: formatFromVariableSelect(settings['video-data-src']) || '',
    }

    // Video Title
    if (settings['video-data-title']) {
      const title = formatFromVariableText(settings['video-data-title'])
      if (title.length > 0 && title[0] !== 'text:') {
        result.videoTitle = title
      }
    }

    // Controls
    if (settings['video-design-controls'] !== undefined && settings['video-design-controls'] !== template['video-design-controls']) {
      result.controls = settings['video-design-controls']
    }

    // Volume
    if (settings['video-design-volume'] !== undefined && settings['video-design-volume'] !== template['video-design-volume']) {
      result.volume = settings['video-design-volume']
    }

    // Width - extract from OverflowSettings
    if (settings['video-design-width']) {
      const width = formatWidthFormat(settings['video-design-width'] as any)
      if (width) {
        result.width = width
      }
    }

    // Margin - extract from OverflowSettings
    if (settings['video-design-margin']) {
      const margin = formatSpacingFormat(settings['video-design-margin'] as any)
      if (margin) {
        result.margin = margin
      }
    }

    // Padding
    const padding = formatSpacingFormat(settings['video-design-padding'])
    if (padding) {
      result.padding = padding
    }

    // Alignment
    if (settings['video-design-h-align']) {
      const textAlign = settings['video-design-h-align'].phone
      if (textAlign && (textAlign as any) !== 'parent') {
        const alignMap: Record<string, string> = {
          left: 'h:start',
          right: 'h:end',
          center: 'h:center',
        }
        result.align = alignMap[textAlign] || 'h:center'
      }
    }

    // Corner Radius
    const cornerRadius = formatCornerRadiusFormat(settings['video-design-shape'])
    if (cornerRadius && cornerRadius !== template['video-design-shape']) {
      result.cornerRadius = cornerRadius
    }

    // Aspect Ratio
    const aspectRatio = formatAspectRatioFormat(settings['video-design-ratio'] as any)
    if (aspectRatio) {
      result.aspectRatio = aspectRatio
    }

    // Hover Effect
    if (settings['video-design-hover-effect']) {
      const hoverEffect = settings['video-design-hover-effect']
      if (hoverEffect && hoverEffect !== 'none') {
        result.hoverEffect = hoverEffect
      }
    }

    return result
  }

  /**
   * parse AI video input to video component settings
   */
  parse(input: AiVideoInput): IVideoComponentSettings {
    const result = deepMerge(VIDEO_DEFAULT_TEMPLATE, {})

    // Data - Video source (required)
    if (input.src) {
      result['video-data-src'] = {
        id: uuidv4(),
        value: input.src,
        flag: VariableSelectFeatureFlag.CustomText,
      }
    }

    if (input.videoTitle) {
      result['video-data-title'] = parseToVariableText(input.videoTitle)
    }

    // Design - Controls
    if (input.controls !== undefined) {
      result['video-design-controls'] = input.controls
    }

    if (input.volume !== undefined) {
      result['video-design-volume'] = input.volume
    }

    // Design - Width
    if (input.width) {
      result['video-design-width'] = {
        ...parseWidthFormat(input.width),
        overflowSetting: result['video-design-width'].overflowSetting,
      }
    }

    // Design - Margin
    if (input.margin) {
      result['video-design-margin'] = {
        ...parseSpacingFormat(input.margin),
        overflowSetting: result['video-design-margin'].overflowSetting,
      }
    }

    // Design - Padding
    if (input.padding) {
      result['video-design-padding'] = parseSpacingFormat(input.padding)
    }

    // Design - Alignment
    if (input.align) {
      const alignmentParsed = parseAlignmentFormat(input.align)
      if (alignmentParsed.hAlign) {
        // Map ComponentAlignment to TextAlignment
        const mappedTextAlign = Object.fromEntries(
          Object.entries(alignmentParsed.hAlign).map(([key, value]) => {
            let textAlign: TextAlignment
            if (value === 'start') textAlign = 'left'
            else if (value === 'end') textAlign = 'right'
            else textAlign = 'center'
            return [key, textAlign]
          })
        ) as ResponsiveValue<TextAlignment>
        result['video-design-h-align'] = mappedTextAlign
      }
      if (alignmentParsed.textAlign) {
        result['video-design-h-align'] = alignmentParsed.textAlign
      }
    }

    // Design - Corner Radius
    if (input.cornerRadius) {
      result['video-design-shape'] = parseCornerRadiusFormat(input.cornerRadius).phone
    }

    // Design - Aspect Ratio
    if (input.aspectRatio) {
      result['video-design-ratio'] = parseAspectRatioFormat(input.aspectRatio)
    }

    // Hover Effect
    if (input.hoverEffect) {
      result['video-design-hover-effect'] = input.hoverEffect
    }

    return result
  }
}
